import type { Database } from "@/lib/supabase";

export type AccountContact = Database["public"]["Tables"]["account_contacts"]["Row"];

export interface MessageVariableDefinition {
  /** Token exatamente como o usuário deve digitar / colar. */
  token: string;
  /** Texto curto exibido como tooltip e legenda. */
  description: string;
  /** Valor mostrado em previews quando não há contato real. */
  sample: string;
  /** Regex case-insensitive que tolera espaços internos ao token. */
  pattern: RegExp;
  /** Resolve o valor real a partir de um contato. */
  resolve: (contact: AccountContact) => string;
}

const safeJidLocal = (jid: string | null | undefined): string => {
  if (!jid) return "";
  const at = jid.indexOf("@");
  return at >= 0 ? jid.slice(0, at) : jid;
};

const resolveNome = (c: AccountContact): string =>
  c.display_name?.trim() ||
  c.push_name_last?.trim() ||
  c.name?.trim() ||
  safeJidLocal(c.jid);

const resolvePrimeiroNome = (c: AccountContact): string => {
  const nome = resolveNome(c);
  return nome.split(/\s+/)[0] ?? "";
};

const resolveTelefone = (c: AccountContact): string =>
  c.phone?.trim() || safeJidLocal(c.jid);

/**
 * Mantém paridade exata com o renderer Postgres
 * `public.render_scheduled_message` (migration 20260513000000).
 *
 * Use o mesmo conjunto em qualquer lugar que aceite variáveis em mensagens
 * (Respostas Rápidas, Mensagens Agendadas, etc).
 */
export const MESSAGE_VARIABLES: MessageVariableDefinition[] = [
  {
    token: "{{ nome }}",
    description: "Nome do contato",
    sample: "João Silva",
    pattern: /\{\{\s*nome\s*\}\}/gi,
    resolve: resolveNome,
  },
  {
    token: "{{ primeiro_nome }}",
    description: "Primeiro nome",
    sample: "João",
    pattern: /\{\{\s*primeiro_nome\s*\}\}/gi,
    resolve: resolvePrimeiroNome,
  },
  {
    token: "{{ telefone }}",
    description: "Telefone",
    sample: "+55 11 99999-0000",
    pattern: /\{\{\s*telefone\s*\}\}/gi,
    resolve: resolveTelefone,
  },
  {
    token: "{{ email }}",
    description: "E-mail",
    sample: "joao@email.com",
    pattern: /\{\{\s*email\s*\}\}/gi,
    resolve: (c) => c.email?.trim() ?? "",
  },
  {
    token: "{{ jid }}",
    description: "ID do WhatsApp",
    sample: "5511999990000@s.whatsapp.net",
    pattern: /\{\{\s*jid\s*\}\}/gi,
    resolve: (c) => c.jid ?? "",
  },
];

/**
 * Substitui as variáveis suportadas pelo conteúdo derivado do contato.
 * Espelha `public.render_scheduled_message`. Se `contact` for nulo, devolve o
 * conteúdo original sem alterações (igual ao comportamento do RPC).
 */
export function renderMessageVariables(
  content: string,
  contact: AccountContact | null | undefined
): string {
  if (!content) return content ?? "";
  if (!contact) return content;
  return MESSAGE_VARIABLES.reduce(
    (acc, v) => acc.replace(v.pattern, () => v.resolve(contact) ?? ""),
    content
  );
}

/** Indica se algum dos tokens conhecidos aparece no conteúdo. */
export function hasMessageVariables(content: string | null | undefined): boolean {
  if (!content) return false;
  return MESSAGE_VARIABLES.some((v) => {
    v.pattern.lastIndex = 0;
    return v.pattern.test(content);
  });
}

/**
 * Contato fictício com os valores `sample` de cada variável. Útil para
 * pré-visualizar mensagens em telas onde não há um contato real (ex.: editor
 * de Respostas Rápidas em Configurações).
 */
export const SAMPLE_CONTACT: AccountContact = {
  id: "00000000-0000-0000-0000-000000000000",
  account_id: "00000000-0000-0000-0000-000000000000",
  jid: MESSAGE_VARIABLES.find((v) => v.token === "{{ jid }}")?.sample ?? "",
  is_group: false,
  name: null,
  display_name: MESSAGE_VARIABLES.find((v) => v.token === "{{ nome }}")?.sample ?? null,
  push_name_last: null,
  email: MESSAGE_VARIABLES.find((v) => v.token === "{{ email }}")?.sample ?? null,
  phone: MESSAGE_VARIABLES.find((v) => v.token === "{{ telefone }}")?.sample ?? null,
  account_assistant_id: null,
  assistant_is_active: null,
  attribution_id: null,
  last_message_at: null,
  last_message_id: null,
  last_read_at: null,
  profile_picture: null,
  profile_picture_updated_at: null,
  created_at: null,
  updated_at: null,
};
